<template>
  <span>
    <!-- Home shows its own button; other pages open the modal themselves (buttonType 'none') -->
    <button v-if="buttonType !== 'none'"
            type="button"
            class="btn btn-secondary"
            @click="showCreatePartitionModal"
    >
      <font-awesome-icon icon="layer-group" class="color-secondary mr-2"/>
      {{ $t('administration.partitions.create_button') }}
    </button>

    <!-- Create or edit a partition (US 9365) -->
    <b-modal ref="partition"
             modal-class="v2-modal us-partition-modal"
             hide-footer
             @shown="focusName"
    >
      <template #modal-header>
        <div class="us-modal-head">
          <div class="pt-modal-title">
            <WorkspaceAvatar :workspace="{ name: form.name, parent: true }" :size="40" :partition="true"/>
            <div>
              <h5 class="mb-0" qid="partition-modal-title">{{ editing ? $t('partitions_page.form.edit_title') : $t('partitions_page.form.create_title') }}</h5>
              <p class="us-modal-head__sub">{{ editing ? form.name : $t('partitions_page.form.create_subtitle', { client: parentName }) }}</p>
            </div>
          </div>
          <button type="button"
                  class="us-icon-btn"
                  :aria-label="$t('administration.partitions.create.cancel')"
                  @click="hide"
          >
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>

      <b-alert v-if="error"
               show
               variant="danger"
               qid="partition-modal-error"
      >
        {{ error }}
        <span v-if="showGetMore" class="text-success cursor-pointer" @click="goToBilling">
          {{ $t('administration.users.create.get_more_label') }}
        </span>
      </b-alert>

      <b-form class="us-form" qid="partition-form" @submit.prevent="submit">
        <div class="us-form__row us-form__row--wide">
          <b-form-group class="mb-0">
            <label class="us-field-label" for="partition-name">{{ $t('administration.partitions.create.name_label') }}<span class="mandatory"> *</span></label>
            <b-form-input id="partition-name"
                          ref="name"
                          v-model="form.name"
                          :maxLength="255"
                          required
                          :placeholder="$t('administration.partitions.create.name_placeholder')"
                          qid="partition-form-name"
            ></b-form-input>
          </b-form-group>
          <b-form-group class="mb-0">
            <label class="us-field-label" for="partition-reference">{{ $t('administration.partitions.create.reference_label') }}</label>
            <b-form-input id="partition-reference"
                          v-model="form.reference_number"
                          :placeholder="$t('users_page.invite.optional')"
                          qid="partition-form-reference"
            ></b-form-input>
          </b-form-group>
        </div>

        <b-form-group class="mb-0">
          <label class="us-field-label" for="partition-cvr">{{ $t('administration.partitions.create.cvr_label') }}</label>
          <b-form-input id="partition-cvr"
                        v-model="form.cvr"
                        :placeholder="$t('users_page.invite.optional')"
                        qid="partition-form-cvr"
          ></b-form-input>
        </b-form-group>

        <fieldset class="pt-address">
          <legend class="us-drawer__label">{{ $t('partitions_page.form.address') }}</legend>
          <b-form-input v-model="form.street"
                        :placeholder="$t('administration.partitions.create.street_label')"
                        :aria-label="$t('administration.partitions.create.street_label')"
                        qid="partition-form-street"
          ></b-form-input>
          <div class="pt-address__row">
            <b-form-input v-model="form.postcode"
                          :placeholder="$t('administration.partitions.create.postcode_label')"
                          :aria-label="$t('administration.partitions.create.postcode_label')"
                          qid="partition-form-postcode"
            ></b-form-input>
            <b-form-input v-model="form.city"
                          :placeholder="$t('administration.partitions.create.city_label')"
                          :aria-label="$t('administration.partitions.create.city_label')"
                          qid="partition-form-city"
            ></b-form-input>
          </div>
          <multiselect v-model="selectedCountry"
                       :options="countries"
                       track-by="name"
                       label="name"
                       :placeholder="$t('administration.partitions.create.country_label')"
                       :select-label="$t('system.dropdown_select_label')"
                       :selected-label="$t('system.dropdown_selected_label')"
                       :deselect-label="$t('system.dropdown_deselect_label')"
                       qid="partition-form-country"
          >
            <template slot="singleLabel" slot-scope="props">
              <img :src="props.option.flag"
                   height="13"
                   class="align-baseline mr-2"
                   alt=""
              >{{ props.option.name }}
            </template>
            <template slot="option" slot-scope="props">
              <img :src="props.option.flag"
                   height="13"
                   class="align-baseline mr-2"
                   alt=""
              >{{ props.option.name }}
            </template>
          </multiselect>
        </fieldset>

        <div class="us-modal-foot">
          <span class="us-modal-foot__note">
            <span v-if="!editing">{{ $t('partitions_page.form.you_become_admin') }}</span>
          </span>
          <b-button variant="secondary" qid="partition-form-cancel" @click="hide">
            {{ $t('administration.partitions.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    variant="success"
                    :disabled="busy"
                    qid="partition-form-submit"
          >
            <font-awesome-icon v-if="busy" icon="spinner" class="fa-spin mr-1"/>
            {{ editing ? $t('users_page.edit.save') : $t('partitions_page.form.create') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </span>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import WorkspaceAvatar from '@/components/navigation/WorkspaceAvatar.vue';

  const EMPTY = { slug: '', name: '', cvr: '', street: '', city: '', postcode: '', country: '', reference_number: '' };

  export default {
    name: 'PartitionFormModal',
    components: {
      WorkspaceAvatar
    },
    props: {
      buttonType: String,
      // Home moves into the new partition straight away
      switchAfterCreate: Boolean
    },
    data() {
      return {
        form: Object.assign({}, EMPTY),
        editing: false,
        countries: [],
        selectedCountry: null,
        error: '',
        showGetMore: false,
        busy: false
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client;
      },
      parentName() {
        return this.getClient.parent ? this.getClient.parent.name : this.getClient.name;
      }
    },
    methods: {
      loadCountries() {
        let self = this;
        if (this.countries.length) {
          return;
        }
        piincHttp.get('countries-with-flag').then(function(response) {
          self.countries = response.data;
          self.matchCountry();
        }, function() {});
      },
      matchCountry() {
        let name = this.form.country;
        this.selectedCountry = name ? (_.find(this.countries, { name: name }) || null) : null;
      },
      showCreatePartitionModal() {
        this.open(Object.assign({}, EMPTY), false);
      },
      showEditPartitionModal(partition) {
        this.open(_.pick(Object.assign({}, EMPTY, partition), Object.keys(EMPTY)), true);
      },
      open(form, editing) {
        this.form = form;
        this.editing = editing;
        this.error = '';
        this.showGetMore = false;
        this.busy = false;
        this.loadCountries();
        this.matchCountry();
        this.$refs.partition.show();
      },
      hide() {
        this.$refs.partition.hide();
      },
      focusName() {
        if (this.$refs.name) {
          this.$refs.name.focus();
        }
      },
      submit() {
        let self = this;
        let data = _.omit(this.form, ['slug']);
        data.country = this.selectedCountry ? this.selectedCountry.name : '';

        this.busy = true;
        this.error = '';

        let request = this.editing
          ? piincHttp.put('partitions/' + this.form.slug, data)
          : piincHttp.post('partitions', Object.assign(data, { client: this.getClient.slug }));

        request.then(function(response) {
          self.busy = false;
          self.$toastr('success', self.$t(self.editing ? 'administration.partitions.update.success' : 'administration.partitions.create.success'));
          self.hide();
          self.$emit('saved', response.data);
          if (!self.editing && self.switchAfterCreate) {
            self.switchTo(response.data);
          }
        }, function(error) {
          self.busy = false;
          self.error = (error.data && error.data.message) || self.$t('users_page.error');
          if (self.error === 'You are not allowed to add partition. Please check subscription.') {
            self.error = self.$t('administration.users.create.subscription_error_partition');
            self.showGetMore = true;
          }
        });
      },
      // Open the new partition as the current workspace
      switchTo(partition) {
        let self = this;
        piincHttp.get('users/me/permissions/' + partition.slug).then(function(response) {
          partition.user_permissions = response.data.user_permissions;
          partition.user_roles = response.data.user_roles;
          localStorage.setItem('clientObject', JSON.stringify(partition));
          localStorage.setItem('client', partition.slug);
          self.$store.commit('storeClient', partition);
        }, function() {});
      },
      goToBilling() {
        window.location.href = '/client/' + this.getClient.slug + '?tabindex=1';
      }
    }
  }
</script>
